import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Download, CheckCircle2, XCircle } from "lucide-react";
import SignatureCanvas from "react-signature-canvas";
import forge from "node-forge";
import { toast } from "sonner";
import { publicErrorMessage } from "@/lib/publicErrors";
import { PublicErrorCard } from "@/components/signing/PublicErrorCard";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { generateBudgetPdf } from "@/lib/budgetPdf";
import { resolveProposalPdf } from "@/lib/proposalDocument";
import type { Budget } from "@/hooks/useBudgets";
import logoUrl from "@/assets/brand/simbolo.svg";
import { SignatureTabs, type SignatureTab } from "@/components/signing/SignatureTabs";
import { SignersCard } from "@/components/signing/SignersCard";
import { RejectInline } from "@/components/signing/RejectInline";
import { barterSplit, recurringBilling } from "@/lib/barter";
import { formatBRL } from "@/lib/format";
import SEOMeta from "@/components/SEOMeta";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const FN_URL = `${SUPABASE_URL}/functions/v1/sign-contract`;

const fmtCurrency = (v: number) => formatBRL(v);

interface ApiData {
  signature: {
    id: string;
    signer_name: string;
    signer_email: string;
    status: "pending" | "signed" | "rejected";
  };
  contract: {
    title: string;
    generated_pdf_url: string | null;
    richtext_content: string | null;
  } | null;
  budget: Budget | null;
  signers: {
    signer_name: string;
    signer_email: string;
    status: string;
    order_index: number;
    signed_at: string | null;
  }[];
  canSign: boolean;
}

function SignContractView() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ApiData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState<SignatureTab>("drawn");
  const [typedName, setTypedName] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [certFile, setCertFile] = useState<File | null>(null);
  const [certPwd, setCertPwd] = useState("");
  const [lgpdConsent, setLgpdConsent] = useState(false);
  const sigPadRef = useRef<SignatureCanvas>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${FN_URL}?token=${token}`, {
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Erro");
      setData(json);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const submit = async (payload: Record<string, unknown>) => {
    setSubmitting(true);
    try {
      const res = await fetch(FN_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: ANON_KEY,
          Authorization: `Bearer ${ANON_KEY}`,
        },
        body: JSON.stringify({ token, ...payload }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Erro");
      toast.success(
        payload.action === "reject"
          ? "Recusa registrada. Eliveuton será avisado."
          : "Assinatura registrada com sucesso! Você receberá o documento assinado por e-mail.",
      );
      await fetchData();
    } catch (e) {
      toast.error(publicErrorMessage(e instanceof Error ? e.message : null));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSign = async () => {
    if (tab === "drawn") {
      if (!sigPadRef.current || sigPadRef.current.isEmpty())
        return toast.error("Desenhe sua assinatura");
      const dataUrl = sigPadRef.current.getCanvas().toDataURL("image/png");
      await submit({ signature_type: "drawn", signature_data: dataUrl });
    } else if (tab === "typed") {
      if (typedName.trim().length < 3) return toast.error("Digite seu nome completo");
      if (typedName.trim().toLowerCase() !== data?.signature.signer_name.trim().toLowerCase()) {
        return toast.error("O nome digitado deve corresponder ao nome do signatário");
      }
      await submit({ signature_type: "typed", signature_data: typedName.trim() });
    } else if (tab === "certificate") {
      if (!certFile) return toast.error("Selecione o arquivo .p12/.pfx");
      try {
        const buf = await certFile.arrayBuffer();
        const bytes = new Uint8Array(buf);
        let binary = "";
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const der = forge.util.createBuffer(binary, "raw");
        const asn1 = forge.asn1.fromDer(der);
        const p12 = forge.pkcs12.pkcs12FromAsn1(asn1, false, certPwd);
        const bags = p12.getBags({ bagType: forge.pki.oids.certBag });
        const certBag = bags[forge.pki.oids.certBag]?.[0];
        if (!certBag?.cert) throw new Error("Certificado não encontrado");
        const cert = certBag.cert;
        const cn = cert.subject.getField("CN")?.value || "";
        const issuer = cert.issuer.getField("CN")?.value || "";
        const validFrom = cert.validity.notBefore.toISOString();
        const validTo = cert.validity.notAfter.toISOString();
        const keyBags = p12.getBags({ bagType: forge.pki.oids.pkcs8ShroudedKeyBag });
        const keyBag = keyBags[forge.pki.oids.pkcs8ShroudedKeyBag]?.[0];
        if (!keyBag?.key) throw new Error("Chave privada não encontrada");
        const md = forge.md.sha256.create();
        const payload = `${token}|${Date.now()}`;
        md.update(payload);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const signature = forge.util.encode64((keyBag.key as any).sign(md));
        await submit({
          signature_type: "certificate",
          signature_data: signature,
          certificate_info: {
            cn,
            issuer,
            validFrom,
            validTo,
            payload,
            pem: forge.pki.certificateToPem(cert),
          },
        });
      } catch (e) {
        toast.error("Erro no certificado: " + (e instanceof Error ? e.message : "inválido"));
      }
    }
  };

  const handleReject = async () => {
    if (rejectReason.trim().length < 3) return toast.error("Informe o motivo da rejeição");
    await submit({ action: "reject", reason: rejectReason.trim() });
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error || !data) {
    return <PublicErrorCard code={error} />;
  }

  const { signature: sig, contract, budget, signers, canSign } = data;
  const done = sig.status !== "pending";
  const showSigning = !done && canSign;

  return (
    <div className="min-h-screen bg-background px-6 pb-16 pt-7">
      <div className="mx-auto flex max-w-[820px] flex-col gap-4">
        <div className="flex items-center gap-3">
          <img src={logoUrl} alt="Eliveuton Souza" className="h-10 w-10 object-contain" />
          <div>
            <p className="font-semibold text-foreground">Eliveuton Souza</p>
            <p className="text-xs text-muted-foreground">Desenvolvimento Web & Automação com IA</p>
          </div>
        </div>

        {budget && (
          <div className="rounded-[18px] border border-border bg-card px-7 py-6 shadow-card">
            <p className="text-[11.5px] font-bold uppercase tracking-[.06em] text-muted-foreground">
              1. Proposta comercial
            </p>
            <h2 className="mt-2 font-display text-lg font-bold tracking-[-.02em] text-foreground">
              {budget.title}
            </h2>
            {budget.description && (
              <p className="mt-[9px] whitespace-pre-wrap text-[13.5px] leading-[1.65] text-muted-foreground">
                {budget.description}
              </p>
            )}
            <div className="mt-[18px] flex flex-col gap-[7px] rounded-[13px] bg-muted px-[18px] py-4">
              {(budget.items ?? []).map((it) => (
                <div key={it.id} className="flex justify-between gap-3 text-[13px]">
                  <span className="min-w-0 truncate text-muted-foreground">
                    {it.title}
                    {it.pricing_mode === "hourly" ? ` · ${it.hours ?? 0}h` : ""}
                    {it.pricing_mode === "recurring" ? " · mensal" : ""}
                  </span>
                  <span className="shrink-0 text-foreground">
                    {fmtCurrency(it.subtotal)}
                    {it.pricing_mode === "recurring" ? "/mês" : ""}
                  </span>
                </div>
              ))}
              {(() => {
                const oneTimeItems = (budget.items ?? []).filter(
                  (i) => i.pricing_mode !== "recurring",
                );
                const itemsGross = oneTimeItems.reduce((s, i) => s + i.subtotal, 0);
                return oneTimeItems.length > 0 && itemsGross > budget.gross_total ? (
                  <div className="flex justify-between text-[13px] text-ok">
                    <span>
                      Desconto do pacote
                      {budget.discount_percent ? ` (${budget.discount_percent}%)` : ""}
                    </span>
                    <span>− {fmtCurrency(itemsGross - budget.gross_total)}</span>
                  </div>
                ) : null;
              })()}
              <div className="flex justify-between text-[13.5px]">
                <span className="text-muted-foreground">Valor bruto</span>
                <span className="text-foreground">{fmtCurrency(budget.gross_total)}</span>
              </div>
              {budget.gross_total !== budget.net_total && (
                <div className="flex justify-between text-[13.5px] text-muted-foreground">
                  <span>Taxa</span>
                  <span>− {fmtCurrency(budget.gross_total - budget.net_total)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-border pt-[9px] text-[14.5px] font-bold">
                <span className="text-foreground">Valor total</span>
                <span className="text-primary">{fmtCurrency(budget.net_total)}</span>
              </div>
              {(() => {
                const barter = barterSplit(budget.net_total, budget.barter_percent ?? 0);
                return barter.barterTotal > 0 ? (
                  <div className="space-y-1" data-testid="contract-barter">
                    <div className="flex justify-between text-[13.5px] text-muted-foreground">
                      <span>Permuta ({barter.barterPercent}%) — em serviços</span>
                      <span>− {fmtCurrency(barter.barterTotal)}</span>
                    </div>
                    <div className="flex justify-between text-[14.5px] font-bold">
                      <span className="text-foreground">Valor a cobrar</span>
                      <span className="text-primary">
                        {fmtCurrency(barter.cashTotal)}
                        {barter.isFull && (
                          <span className="ml-1 text-[11px] font-normal text-muted-foreground">
                            (permuta integral)
                          </span>
                        )}
                      </span>
                    </div>
                    {budget.barter_description && (
                      <p className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-muted-foreground">
                        <span className="font-semibold text-foreground">Contrapartida:</span>{" "}
                        {budget.barter_description}
                      </p>
                    )}
                  </div>
                ) : null;
              })()}
              {budget.recurring_monthly_total > 0 && (
                <div
                  className="flex justify-between text-[13.5px] font-bold"
                  data-testid="contract-recurring"
                >
                  <span className="text-foreground">Mensalidade</span>
                  {recurringBilling(budget).inBarter ? (
                    <span className="text-muted-foreground">
                      {fmtCurrency(budget.recurring_monthly_total)}/mês · incluída na permuta
                    </span>
                  ) : (
                    <span className="text-primary">
                      + {fmtCurrency(budget.recurring_monthly_total)}/mês
                    </span>
                  )}
                </div>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-4 h-[38px] rounded-[10px] border-border bg-card px-[15px] text-[12.5px] font-semibold text-muted-foreground"
              onClick={() => {
                // Same document the client signed/will sign — never the platform default
                // when the operator authored or uploaded one (flows/proposta.md R12).
                const pdf = resolveProposalPdf(budget);
                if (pdf.url) window.open(pdf.url, "_blank");
                else if (pdf.kind === "generated") generateBudgetPdf(budget);
              }}
            >
              <Download className="mr-2 h-3.5 w-3.5" /> Baixar proposta em PDF
            </Button>
          </div>
        )}

        <div className="rounded-[18px] border border-border bg-card px-7 py-6 shadow-card">
          <div className="flex flex-wrap items-start justify-between gap-3.5">
            <div>
              <p className="text-[11.5px] font-bold uppercase tracking-[.06em] text-muted-foreground">
                {budget ? "2. Contrato" : "Contrato para assinatura"}
              </p>
              <h1 className="mt-2 font-display text-xl font-bold tracking-[-.03em] text-foreground">
                {contract?.title}
              </h1>
              <p className="mt-[7px] text-[13px] text-muted-foreground">
                Para: <strong className="text-foreground">{sig.signer_name}</strong> (
                {sig.signer_email})
              </p>
            </div>
            {done ? (
              <span
                className={`inline-flex h-[25px] items-center whitespace-nowrap rounded-lg px-[11px] text-[11.5px] font-bold ${
                  sig.status === "signed" ? "bg-ok-soft text-ok" : "bg-bad-soft text-bad"
                }`}
              >
                {sig.status === "signed" ? "Assinado" : "Rejeitado"}
              </span>
            ) : (
              <span className="inline-flex h-[25px] items-center whitespace-nowrap rounded-lg bg-accent px-[11px] text-[11.5px] font-bold text-primary">
                Aguardando assinatura
              </span>
            )}
          </div>

          {contract?.richtext_content ? (
            <div className="prose prose-sm mt-5 max-h-[300px] max-w-none overflow-y-auto rounded-[13px] border border-border bg-background px-[22px] py-5 text-[13.5px] leading-[1.8] text-muted-foreground [&_h1]:text-[13px] [&_h1]:font-bold [&_h1]:text-foreground [&_h2]:text-[13px] [&_h2]:font-bold [&_h2]:text-foreground [&_h3]:text-[13px] [&_h3]:font-bold [&_h3]:text-foreground [&_p]:my-1.5">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{contract.richtext_content}</ReactMarkdown>
            </div>
          ) : contract?.generated_pdf_url ? (
            <div className="mt-5 overflow-hidden rounded-[13px] border border-border bg-muted">
              <iframe
                src={contract.generated_pdf_url}
                className="h-[520px] w-full"
                title="Contrato para assinatura"
              />
            </div>
          ) : null}

          {contract?.generated_pdf_url && (
            <a
              href={contract.generated_pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-muted-foreground hover:text-foreground"
            >
              <Download className="h-3.5 w-3.5" /> Baixar contrato em PDF
            </a>
          )}

          {showSigning && (
            <>
              <div className="mt-[22px]">
                <SignatureTabs
                  tab={tab}
                  onTabChange={setTab}
                  signerName={sig.signer_name}
                  typedName={typedName}
                  onTypedNameChange={setTypedName}
                  certFile={certFile}
                  onCertFileChange={setCertFile}
                  certPwd={certPwd}
                  onCertPwdChange={setCertPwd}
                  sigPadRef={sigPadRef}
                  canvasHeight={160}
                  certHint="O arquivo é lido no seu navegador e não é enviado"
                />
              </div>

              <div className="mt-[18px] rounded-xl border border-border bg-muted px-4 py-3.5 text-[11.5px] leading-[1.6] text-muted-foreground">
                Registramos nome, e-mail, assinatura, endereço IP e navegador para comprovar o
                aceite, conforme a Lei nº 14.063/2020. Uma cópia em PDF vai para o seu e-mail.
                <label
                  htmlFor="lgpd-consent"
                  className="mt-2.5 flex cursor-pointer items-start gap-2.5"
                >
                  <Checkbox
                    id="lgpd-consent"
                    checked={lgpdConsent}
                    onCheckedChange={(v) => setLgpdConsent(!!v)}
                    className="mt-0.5"
                  />
                  <span>
                    Concordo com o tratamento da minha assinatura eletrônica conforme a Lei nº
                    14.063/2020 e a LGPD (Lei nº 13.709/2018).
                  </span>
                </label>
              </div>

              <div className="mt-[18px] flex flex-wrap gap-2.5">
                <Button
                  onClick={handleSign}
                  disabled={submitting || !lgpdConsent}
                  className="h-[50px] min-w-[200px] flex-1 rounded-[13px] text-[15px] font-bold"
                >
                  {submitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                  )}
                  Assinar contrato
                </Button>
                <Button
                  variant="outline"
                  className="h-[50px] rounded-[13px] border-border bg-card px-5 text-[13.5px] font-semibold text-muted-foreground"
                  onClick={() => setShowReject((v) => !v)}
                  disabled={submitting}
                >
                  Rejeitar
                </Button>
              </div>

              {showReject && (
                <RejectInline
                  reason={rejectReason}
                  onReasonChange={setRejectReason}
                  onSubmit={handleReject}
                  submitting={submitting}
                  placeholder="Explique o que precisa mudar para eu ajustar o contrato."
                />
              )}
            </>
          )}
        </div>

        {!done && !canSign && (
          <div className="rounded-[18px] border border-border bg-card p-6 text-center text-sm text-muted-foreground shadow-card">
            Aguardando assinatura de signatários anteriores. Você receberá um novo aviso quando for
            sua vez.
          </div>
        )}

        {done && (
          <div
            className={`rounded-[18px] border p-8 text-center shadow-card ${sig.status === "signed" ? "border-ok bg-ok-soft" : "border-border bg-card"}`}
          >
            {sig.status === "signed" ? (
              <>
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-ok text-ok-foreground">
                  <CheckCircle2 className="h-7 w-7" />
                </span>
                <p className="mt-3 font-medium text-foreground">
                  Sua assinatura foi registrada. Obrigado!
                </p>
              </>
            ) : (
              <>
                <XCircle className="mx-auto mb-2 h-12 w-12 text-destructive" />
                <p className="font-medium text-foreground">Contrato rejeitado.</p>
              </>
            )}
          </div>
        )}

        <SignersCard title="Signatários" signers={signers} currentEmail={sig.signer_email} />
      </div>
    </div>
  );
}

// Página por token: nunca indexar nem gerar preview com dado do cliente
// (docs/specs/standards/pages-and-routing.md, regra 2).
export default function SignContract() {
  return (
    <>
      <SEOMeta
        title="Assinatura de contrato"
        description="Página privada de assinatura de contrato."
        noindex
      />
      <SignContractView />
    </>
  );
}
